import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import {
  CheckCircle as ResolvedIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const statusColor = (status) => {
  if (status === 'open') return 'error';
  if (status === 'in_progress') return 'warning';
  if (status === 'resolved') return 'success';
  return 'default';
};

const statusLabel = (status) => {
  if (status === 'open') return 'Open';
  if (status === 'in_progress') return 'In Progress';
  if (status === 'resolved') return 'Resolved';
  return status;
};

const TicketsAdminPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0); // 0=all, 1=open, 2=in_progress, 3=resolved
  const [viewTicket, setViewTicket] = useState(null);
  const [updateDialog, setUpdateDialog] = useState({ open: false, ticket: null });
  const [updateForm, setUpdateForm] = useState({ status: '', adminNotes: '' });
  const [updating, setUpdating] = useState(false);
  const [responseDialog, setResponseDialog] = useState({ open: false, type: '', message: '' });

  const tabStatuses = [undefined, 'open', 'in_progress', 'resolved'];

  useEffect(() => {
    fetchTickets();
  }, [tab]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const status = tabStatuses[tab];
      const url = status
        ? `${API_BASE_URL}/tickets?status=${status}`
        : `${API_BASE_URL}/tickets`;

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(res.data.data.tickets);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const openUpdateDialog = (ticket) => {
    setUpdateForm({ status: ticket.status, adminNotes: ticket.adminNotes || '' });
    setUpdateDialog({ open: true, ticket });
  };

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_BASE_URL}/tickets/${updateDialog.ticket.id}/status`,
        updateForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUpdateDialog({ open: false, ticket: null });
      fetchTickets();
      setResponseDialog({ open: true, type: 'success', message: 'Ticket updated successfully!' });
    } catch (err) {
      setResponseDialog({ open: true, type: 'error', message: err.response?.data?.message || 'Failed to update ticket.' });
    } finally {
      setUpdating(false);
    }
  };

  const counts = {
    all: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Support Tickets</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Manage and resolve support tickets raised by the team.
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: counts.all, color: '#64A8ED' },
          { label: 'Open', value: counts.open, color: '#EF4444' },
          { label: 'In Progress', value: counts.in_progress, color: '#F59E0B' },
          { label: 'Resolved', value: counts.resolved, color: '#10B981' }
        ].map(card => (
          <Grid item xs={6} sm={3} key={card.label}>
            <Paper sx={{ p: 2, textAlign: 'center', borderTop: `3px solid ${card.color}` }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: card.color }}>{card.value}</Typography>
              <Typography variant="body2" color="text.secondary">{card.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab label="All" />
          <Tab label="Open" />
          <Tab label="In Progress" />
          <Tab label="Resolved" />
        </Tabs>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ticket #</TableCell>
              <TableCell>Raised By</TableCell>
              <TableCell>Section</TableCell>
              <TableCell>Issue Type</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center"><CircularProgress /></TableCell>
              </TableRow>
            ) : tickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">No tickets found</TableCell>
              </TableRow>
            ) : (
              tickets.map(ticket => (
                <TableRow key={ticket.id} hover>
                  <TableCell><strong>{ticket.ticketNumber}</strong></TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{ticket.user?.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{ticket.user?.email}</Typography>
                  </TableCell>
                  <TableCell>{ticket.section}</TableCell>
                  <TableCell>{ticket.issueType}</TableCell>
                  <TableCell>{new Date(ticket.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip label={statusLabel(ticket.status)} color={statusColor(ticket.status)} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => setViewTicket(ticket)}
                      sx={{ mr: 1 }}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<ResolvedIcon />}
                      onClick={() => openUpdateDialog(ticket)}
                      disabled={ticket.status === 'resolved'}
                      color={ticket.status === 'open' ? 'warning' : 'success'}
                    >
                      Update
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Ticket Dialog */}
      <Dialog open={!!viewTicket} onClose={() => setViewTicket(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          🎫 {viewTicket?.ticketNumber}
          <Chip
            label={statusLabel(viewTicket?.status)}
            color={statusColor(viewTicket?.status)}
            size="small"
            sx={{ ml: 2 }}
          />
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Raised By</Typography>
              <Typography variant="body2" fontWeight={600}>{viewTicket?.user?.name}</Typography>
              <Typography variant="caption" color="text.secondary">{viewTicket?.user?.email}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Role</Typography>
              <Typography variant="body2" fontWeight={600}>{viewTicket?.user?.role}</Typography>
            </Grid>
            <Grid item xs={6} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Section</Typography>
              <Typography variant="body2" fontWeight={600}>{viewTicket?.section}</Typography>
            </Grid>
            <Grid item xs={6} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Issue Type</Typography>
              <Typography variant="body2" fontWeight={600}>{viewTicket?.issueType}</Typography>
            </Grid>
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Description</Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: 'grey.50' }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{viewTicket?.description}</Typography>
              </Paper>
            </Grid>
            {viewTicket?.screenshot && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Screenshot</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <img
                    src={viewTicket.screenshot}
                    alt="Screenshot"
                    style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid #eee', cursor: 'pointer' }}
                    onClick={() => window.open(viewTicket.screenshot, '_blank')}
                  />
                </Box>
              </Grid>
            )}
            {viewTicket?.adminNotes && (
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Alert severity="success">
                  <Typography variant="caption" fontWeight={600}>Resolution Notes:</Typography>
                  <Typography variant="body2">{viewTicket.adminNotes}</Typography>
                </Alert>
              </Grid>
            )}
            {viewTicket?.resolvedAt && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Resolved on: {new Date(viewTicket.resolvedAt).toLocaleString()}
                  {viewTicket?.resolver ? ` by ${viewTicket.resolver.name}` : ''}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewTicket(null)}>Close</Button>
          {viewTicket?.status !== 'resolved' && (
            <Button variant="contained" onClick={() => { setViewTicket(null); openUpdateDialog(viewTicket); }}>
              Update Status
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={updateDialog.open} onClose={() => setUpdateDialog({ open: false, ticket: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Update Ticket: {updateDialog.ticket?.ticketNumber}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select fullWidth label="Status *"
                value={updateForm.status}
                onChange={(e) => setUpdateForm(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={3}
                label="Resolution Notes (Optional)"
                value={updateForm.adminNotes}
                onChange={(e) => setUpdateForm(prev => ({ ...prev, adminNotes: e.target.value }))}
                placeholder="Describe how the issue was resolved. This will be sent to the user when marked as Resolved."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialog({ open: false, ticket: null })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={updating}
            startIcon={updating ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {updating ? 'Updating...' : 'Update Ticket'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Response Dialog */}
      <Dialog open={responseDialog.open} onClose={() => setResponseDialog({ open: false, type: '', message: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>{responseDialog.type === 'success' ? '✅ Success' : '❌ Error'}</DialogTitle>
        <DialogContent>
          <Alert severity={responseDialog.type === 'success' ? 'success' : 'error'} sx={{ mt: 1 }}>
            {responseDialog.message}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setResponseDialog({ open: false, type: '', message: '' })}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TicketsAdminPage;
